import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Loader2, Upload, AlertTriangle, CheckCircle2, Trash2,
} from "lucide-react";

import { API } from "../lib/api";
import { errMsg } from "../lib/errors";
import { renderMarkdown } from "../lib/markdown";
import ChameleonIcon from "../components/ChameleonIcon";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import DisclaimerFooterBar from "../components/DisclaimerFooterBar";

/**
 * Public Specialist Portal — token-authenticated, NO JWT required.
 * Specialist receives a /collab/portal/<token> link from the GP, reads the
 * referring case context, attaches their notes/files, and submits.
 */
export default function SpecialistPortal() {
  const { token } = useParams();
  const fileInputRef = useRef(null);
  const [pkg, setPkg] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    specialist_name: "",
    specialist_license: "",
    notes_markdown: "",
    treatment_sequence: "",
    timeline_estimate: "",
  });
  const [uploads, setUploads] = useState([]); // { id, name, size, category }
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const portalApi = axios.create({ baseURL: API });

  useEffect(() => {
    (async () => {
      try {
        const r = await portalApi.get(`/collab/portal/${token}`);
        setPkg(r.data);
      } catch (err) {
        setLoadErr(errMsg(err, "Invalid or expired link"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [token]);

  const upload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    for (const f of picked) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await portalApi.post(`/collab/portal/${token}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUploads((u) => [...u, r.data]);
      } catch (err) {
        toast.error(errMsg(err, `Upload of ${f.name} failed`));
      }
    }
    e.target.value = "";
  };

  const removeUpload = (id) => setUploads((u) => u.filter((x) => x.id !== id));

  const submit = async () => {
    if (form.specialist_name.trim().length < 1) {
      toast.error("Please enter your name");
      return;
    }
    if (form.notes_markdown.trim().length < 10) {
      toast.error("Clinical notes must be at least 10 characters");
      return;
    }
    setSubmitting(true);
    try {
      await portalApi.post(`/collab/portal/${token}/submit`, {
        ...form,
        file_ids: uploads.map((u) => u.id),
      });
      setSuccess(true);
    } catch (err) {
      toast.error(errMsg(err, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--pa-bg)] text-[var(--pa-text-muted)]">
        <Loader2 size={16} className="animate-spin text-[var(--pa-gold)]" />
      </div>
    );
  }
  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--pa-bg)] px-6">
        <div className="max-w-md p-8 bg-[var(--pa-surface)] border border-red-500/40 rounded-lg text-center">
          <AlertTriangle size={28} className="text-red-400 mx-auto" />
          <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-3">Link unavailable</h2>
          <p className="text-sm text-[var(--pa-text-muted)] mt-2">{loadErr}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--pa-bg)] px-6">
        <div className="max-w-md p-8 bg-[var(--pa-surface)] border border-emerald-500/40 rounded-lg text-center">
          <CheckCircle2 size={32} className="text-emerald-400 mx-auto" />
          <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-3">
            Submission received
          </h2>
          <p className="text-sm text-[var(--pa-text-muted)] mt-3 leading-relaxed">
            Thank you, <strong>Dr. {form.specialist_name}</strong>. Your recommendations have
            been securely transmitted to the referring clinician and will be combined with
            other specialist input under prosthodontic oversight.
          </p>
        </div>
      </div>
    );
  }

  if (pkg?.already_submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--pa-bg)] px-6">
        <div className="max-w-md p-8 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg text-center">
          <ShieldCheck size={28} className="text-[var(--pa-gold)] mx-auto" />
          <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-3">
            Submission already received
          </h2>
          <p className="text-sm text-[var(--pa-text-muted)] mt-2">
            This invitation has already been completed. Please contact the referring clinician
            to amend your input.
          </p>
        </div>
      </div>
    );
  }

  const cs = pkg.case_summary || {};

  return (
    <div className="min-h-screen bg-[var(--pa-bg)]">
      <header className="border-b border-[var(--pa-border)] bg-[var(--pa-bg-2)]">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <ChameleonIcon size={22} className="text-[var(--pa-gold)]" />
          <div>
            <div className="font-serif text-xl text-[var(--pa-text)]">
              Prostho<span className="text-[var(--pa-gold)]">AI</span> · Specialist Portal
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pa-text-dim)] mt-0.5">
              by CamelionAI · Secure link expires{" "}
              {new Date(pkg.expires_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="kicker">
          Specialty: <span className="text-[var(--pa-gold)]">{pkg.specialty_label}</span>
        </div>
        <h1
          data-testid="portal-title"
          className="font-serif text-3xl text-[var(--pa-text)] mt-1"
        >
          Interdisciplinary referral
        </h1>
        <p className="mt-3 text-sm text-[var(--pa-text-muted)] leading-relaxed">
          {cs.referring_clinician?.name || "The referring clinician"} has requested your input
          on the following case. Review the case intake and submit your clinical
          recommendations below. No account required.
        </p>

        {/* GP context */}
        <AiOutputDisclaimer className="mt-6" />
        <section className="mt-4 p-5 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg">
          <div className="kicker">Presenting concern</div>
          <p className="mt-1.5 text-sm text-[var(--pa-text)] whitespace-pre-wrap">
            {cs.presenting_concern || "—"}
          </p>
          <div className="mt-3 text-[11px] text-[var(--pa-text-dim)]">
            {cs.module && <>Module: {cs.module} · </>}
            {cs.patient_name && <>Patient: {cs.patient_name} · </>}
            Referring: {cs.referring_clinician?.name || "—"}
          </div>
        </section>

        {cs.facial_analysis && (
          <details className="mt-4 p-5 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg">
            <summary className="kicker cursor-pointer text-[var(--pa-gold)]">
              Facial Analysis
            </summary>
            <div
              className="prose-clinical mt-3 text-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(cs.facial_analysis) }}
            />
          </details>
        )}
        {cs.occlusal_analysis && (
          <details className="mt-4 p-5 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg">
            <summary className="kicker cursor-pointer text-[var(--pa-gold)]">
              Occlusal Analysis
            </summary>
            <div
              className="prose-clinical mt-3 text-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(cs.occlusal_analysis) }}
            />
          </details>
        )}

        {cs.relevant_findings?.length > 0 && (
          <section className="mt-4 p-5 bg-[var(--pa-surface)] border border-[var(--pa-gold)]/40 rounded-lg">
            <div className="kicker text-[var(--pa-gold)]">Findings relevant to your specialty</div>
            <ul className="mt-3 space-y-2 text-sm text-[var(--pa-text)]">
              {cs.relevant_findings.slice(0, 12).map((f, i) => (
                <li
                  key={i}
                  className="p-2.5 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded"
                >
                  <span className="text-[10px] uppercase tracking-wider text-[var(--pa-gold)]">
                    {f.urgency || "—"}
                  </span>
                  · <span className="text-[var(--pa-text)]">{f.area || ""}</span>
                  {f.finding && <div className="text-xs text-[var(--pa-text-muted)] mt-1">{f.finding}</div>}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] text-[var(--pa-text-dim)]">
              {cs.specialist_review_note}
            </p>
          </section>
        )}

        {/* Submission form */}
        <section
          data-testid="portal-form"
          className="mt-8 p-6 bg-[var(--pa-bg-2)] border border-[var(--pa-gold)]/40 rounded-lg"
        >
          <h2 className="font-serif text-xl text-[var(--pa-text)]">Your clinical input</h2>

          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div>
              <label className="kicker block mb-1">Your name *</label>
              <input
                type="text"
                value={form.specialist_name}
                onChange={(e) => setForm({ ...form, specialist_name: e.target.value })}
                data-testid="portal-name"
                placeholder="Dr. Jane Specialist"
                className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5"
              />
            </div>
            <div>
              <label className="kicker block mb-1">License # (optional)</label>
              <input
                type="text"
                value={form.specialist_license}
                onChange={(e) => setForm({ ...form, specialist_license: e.target.value })}
                data-testid="portal-license"
                placeholder="FL 12345"
                className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="kicker block mb-1">Clinical notes *</label>
            <textarea
              value={form.notes_markdown}
              onChange={(e) => setForm({ ...form, notes_markdown: e.target.value })}
              rows={6}
              data-testid="portal-notes"
              placeholder="Findings, diagnosis, recommendations. Markdown supported."
              className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-3"
            />
          </div>
          <div className="mt-4">
            <label className="kicker block mb-1">Proposed treatment sequence</label>
            <textarea
              value={form.treatment_sequence}
              onChange={(e) => setForm({ ...form, treatment_sequence: e.target.value })}
              rows={3}
              data-testid="portal-sequence"
              placeholder="e.g. 1. SRP quadrant 1, 2. Re-evaluate at 6 wks, 3. ..."
              className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-3"
            />
          </div>
          <div className="mt-4">
            <label className="kicker block mb-1">Timeline estimate</label>
            <input
              type="text"
              value={form.timeline_estimate}
              onChange={(e) => setForm({ ...form, timeline_estimate: e.target.value })}
              data-testid="portal-timeline"
              placeholder="e.g. 12–16 weeks"
              className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5"
            />
          </div>

          {/* File uploads */}
          <div className="mt-5">
            <div className="kicker mb-2">Attach files (X-rays, photos, reports)</div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="portal-upload-btn"
              className="flex items-center gap-2 bg-[var(--pa-surface)] border border-dashed border-[var(--pa-gold)]/50 text-[var(--pa-text)] px-4 py-3 rounded-md hover:border-[var(--pa-gold)] transition w-full justify-center"
            >
              <Upload size={14} /> Add files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={upload}
              className="hidden"
              data-testid="portal-upload-input"
            />
            {uploads.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {uploads.map((u) => (
                  <li
                    key={u.id}
                    data-testid={`portal-upload-${u.id}`}
                    className="flex items-center gap-2 text-xs text-[var(--pa-text-muted)] bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded px-3 py-2"
                  >
                    <span className="flex-1 truncate text-[var(--pa-text)]">
                      {u.original_filename}
                    </span>
                    <span className="text-[10px] text-[var(--pa-gold)] uppercase tracking-wider">
                      {u.category}
                    </span>
                    <button
                      onClick={() => removeUpload(u.id)}
                      className="text-[var(--pa-text-dim)] hover:text-red-400"
                      title="Remove (will persist on server)"
                    >
                      <Trash2 size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="portal-submit"
            className="mt-7 w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><Loader2 size={14} className="animate-spin" /> Submitting…</>
            ) : (
              <><ShieldCheck size={14} /> Submit recommendations</>
            )}
          </button>
          <p className="mt-3 text-[10px] text-[var(--pa-text-dim)] leading-relaxed text-center">
            Your submission is permanently logged with your name, specialty, date, and
            timestamp. Submissions are immutable once received.
          </p>
        </section>

        <div className="mt-8 text-[11px] text-[var(--pa-text-dim)] text-center pt-6 border-t border-[var(--pa-border)]">
          Patent Pending · US Application 64/062,147 · ProsthoAI by Dr. Ronik Seecharan DMD
        </div>
      </main>
      <DisclaimerFooterBar />
    </div>
  );
}
